import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Calendar, ChevronLeft, ChevronRight, Users, AlertCircle, Filter
} from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const LEAVE_COLORS = {
  sick_leave: { bg: 'bg-rose-100', dot: 'bg-rose-500', text: 'text-rose-600', label: 'Sick' },
  casual_leave: { bg: 'bg-blue-100', dot: 'bg-blue-500', text: 'text-blue-600', label: 'Casual' },
  vacation: { bg: 'bg-amber-100', dot: 'bg-amber-500', text: 'text-amber-600', label: 'Vacation' },
  maternity_leave: { bg: 'bg-pink-100', dot: 'bg-pink-500', text: 'text-pink-600', label: 'Maternity' },
  paternity_leave: { bg: 'bg-indigo-100', dot: 'bg-indigo-500', text: 'text-indigo-600', label: 'Paternity' },
  unpaid_leave: { bg: 'bg-slate-100', dot: 'bg-slate-500', text: 'text-slate-600', label: 'Unpaid' },
  earned_leave: { bg: 'bg-emerald-100', dot: 'bg-emerald-500', text: 'text-emerald-600', label: 'Earned' },
  compensatory: { bg: 'bg-cyan-100', dot: 'bg-cyan-500', text: 'text-cyan-600', label: 'Comp Off' },
};

export default function TeamCalendar() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [teamLeaves, setTeamLeaves] = useState([]);
  const [onLeaveToday, setOnLeaveToday] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [deptFilter, setDeptFilter] = useState('');
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    api.get(`/leave/team-calendar?month=${month}&year=${year}`).then(res => {
      setTeamLeaves(res.data.data.leaves);
      setOnLeaveToday(res.data.data.on_leave_today);
      const depts = [...new Set(res.data.data.leaves.map(l => l.user?.department).filter(Boolean))];
      setDepartments(depts);
    }).catch(() => toast.error('Failed to load calendar'));
  }, [month, year]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDay = new Date(year, month - 1, 1).getDay();
  const todayStr = now.toISOString().split('T')[0];

  const filtered = deptFilter ? teamLeaves.filter(l => l.user?.department === deptFilter) : teamLeaves;

  const getLeavesForDay = (day) => {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return filtered.filter(lv => lv.start_date <= dateStr && lv.end_date >= dateStr);
  };

  const prevMonth = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  const selectedDayLeaves = selectedDay ? getLeavesForDay(selectedDay) : [];

  const totalLeavesDays = filtered.filter(l => l.status === 'approved').length;
  const pendingCount = filtered.filter(l => l.status === 'pending').length;
  const uniqueOnLeave = new Set(filtered.filter(l => l.status === 'approved').map(l => l.user_id)).size;

  return (
    <div className="space-y-6" data-testid="team-calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md shadow-blue-500/20">
            <Calendar className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Team Calendar</h1>
            <p className="text-sm text-slate-500">Staff absence overview for your school</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { value: onLeaveToday.length, label: 'On Leave Today', color: 'text-rose-600', bg: 'bg-rose-50 border-rose-100' },
          { value: pendingCount, label: 'Pending Requests', color: 'text-amber-600', bg: 'bg-amber-50 border-amber-100' },
          { value: totalLeavesDays, label: 'Approved This Month', color: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100' },
          { value: uniqueOnLeave, label: 'Staff With Leaves', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-100' },
        ].map((stat, i) => (
          <div key={i} className={`glass-card p-4 text-center ${stat.bg} border`}>
            <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {departments.length > 0 && (
        <div className="flex gap-2 flex-wrap items-center">
          <Filter className="h-4 w-4 text-slate-400" />
          <Button size="sm" variant={!deptFilter ? 'default' : 'outline'} className={!deptFilter ? 'bg-teal-500 text-white rounded-lg' : 'border-slate-200 text-slate-600 rounded-lg'} onClick={() => setDeptFilter('')}>All</Button>
          {departments.map(d => (
            <Button key={d} size="sm" variant={deptFilter === d ? 'default' : 'outline'} className={deptFilter === d ? 'bg-teal-500 text-white rounded-lg' : 'border-slate-200 text-slate-600 rounded-lg'} onClick={() => setDeptFilter(d)}>{d}</Button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass-card p-5" data-testid="calendar-grid">
          <div className="flex items-center justify-between mb-5">
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700" onClick={prevMonth} data-testid="prev-month"><ChevronLeft className="h-4 w-4" /></Button>
            <h2 className="text-lg font-bold text-slate-800">{MONTHS[month - 1]} {year}</h2>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-400 hover:text-slate-700" onClick={nextMonth} data-testid="next-month"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-7 gap-px bg-slate-100 rounded-xl overflow-hidden">
            {DAYS.map(d => (
              <div key={d} className="text-center text-[10px] font-bold text-slate-400 py-2 bg-slate-50 uppercase tracking-wider">{d}</div>
            ))}
            {Array.from({ length: firstDay }, (_, i) => <div key={`e-${i}`} className="h-20 bg-white" />)}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const dayLeaves = getLeavesForDay(day);
              const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const isToday = dateStr === todayStr;
              const isWeekend = new Date(year, month - 1, day).getDay() % 6 === 0;
              const isSelected = selectedDay === day;
              return (
                <div
                  key={day}
                  className={`h-20 p-1.5 bg-white cursor-pointer transition-colors border-t border-slate-100 ${isWeekend ? 'bg-slate-50/50' : ''} ${isToday ? 'ring-2 ring-teal-400 ring-inset' : ''} ${isSelected ? 'bg-teal-50 ring-2 ring-teal-300 ring-inset' : 'hover:bg-slate-50'}`}
                  onClick={() => setSelectedDay(isSelected ? null : day)}
                  data-testid={`day-${day}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs ${isToday ? 'text-teal-600 font-bold' : 'text-slate-400'}`}>{day}</span>
                    {dayLeaves.length > 0 && (
                      <div className="flex gap-0.5">
                        {dayLeaves.some(l => l.status === 'approved') && <span className="h-1.5 w-1.5 rounded-full bg-rose-500" />}
                        {dayLeaves.some(l => l.status === 'pending') && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />}
                      </div>
                    )}
                  </div>
                  <div className="mt-1 space-y-0.5 overflow-hidden max-h-[48px]">
                    {dayLeaves.slice(0, 3).map((lv, j) => {
                      const lc = LEAVE_COLORS[lv.leave_type] || LEAVE_COLORS.casual_leave;
                      return (
                        <div key={j} className={`text-[8px] px-1 py-0.5 rounded ${lv.status === 'pending' ? 'bg-amber-50 text-amber-600 border border-dashed border-amber-300' : `${lc.bg} ${lc.text}`} truncate font-medium`}>
                          {lv.user?.full_name?.split(' ')[0]}
                        </div>
                      );
                    })}
                    {dayLeaves.length > 3 && <div className="text-[8px] text-slate-300 px-1">+{dayLeaves.length - 3}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="glass-card p-5" data-testid="today-panel">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <h3 className="text-sm font-bold text-slate-700">On Leave Today</h3>
              <span className="ml-auto text-xs bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full font-bold">{onLeaveToday.length}</span>
            </div>
            {onLeaveToday.length === 0 ? (
              <p className="text-xs text-slate-400">Everyone is present today</p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {onLeaveToday.map((lv, i) => {
                  const lc = LEAVE_COLORS[lv.leave_type] || LEAVE_COLORS.casual_leave;
                  return (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
                      <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center text-xs font-bold text-white">
                        {lv.user?.full_name?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-700 font-semibold truncate">{lv.user?.full_name}</p>
                        <p className="text-[10px] text-slate-400">{lv.user?.department}</p>
                      </div>
                      <span className={`text-[10px] font-bold ${lc.text}`}>{lc.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {selectedDay && (
            <div className="glass-card p-5" data-testid="day-detail">
              <h3 className="text-sm font-bold text-slate-700 mb-3">{MONTHS[month - 1]} {selectedDay}, {year}</h3>
              {selectedDayLeaves.length === 0 ? (
                <p className="text-xs text-slate-400">No leaves on this day</p>
              ) : (
                <div className="space-y-2">
                  {selectedDayLeaves.map((lv, i) => {
                    const lc = LEAVE_COLORS[lv.leave_type] || LEAVE_COLORS.casual_leave;
                    return (
                      <div key={i} className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                        <div className="flex items-center justify-between mb-1">
                          <p className="text-xs text-slate-700 font-bold">{lv.user?.full_name}</p>
                          <span className={`text-[10px] font-bold capitalize ${lv.status === 'approved' ? 'text-emerald-600' : lv.status === 'rejected' ? 'text-rose-600' : 'text-amber-600'}`}>{lv.status}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-slate-400">
                          <span className={`${lc.text} font-semibold`}>{lc.label}</span>
                          <span>{lv.start_date} to {lv.end_date}</span>
                        </div>
                        {lv.reason && <p className="text-[10px] text-slate-400 mt-1 truncate">{lv.reason}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="glass-card p-4">
            <p className="text-[10px] text-slate-400 uppercase tracking-wider mb-2 font-bold">Legend</p>
            <div className="space-y-1.5">
              {Object.entries(LEAVE_COLORS).slice(0, 6).map(([type, lc]) => (
                <div key={type} className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${lc.dot}`} />
                  <span className="text-[10px] text-slate-500">{lc.label} Leave</span>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-amber-400 border border-dashed border-amber-400" />
                <span className="text-[10px] text-slate-500">Pending Approval</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
